import React from 'react'
import styled from 'styled-components'
import type { TFunction } from 'i18next'
import type { BuildingAdministrative } from '@popety-io/types'
import { Badge } from '../../../atoms/Badge'
import { InfoCard } from '../../../molecules/InfoCard'
import type { InfoCardMetric } from '../../../molecules/InfoCard'
import { Card, CardHeader, CardTitle } from '../EnergyTab.styles'
import { Icon } from '../../../atoms/Icon'
import { DataFreshnessIndicator } from './DataFreshnessIndicator'
import { translateGwrValue } from '../../../../utils/gwrTranslations'

interface HeatingSystemCardProps {
  adminData: BuildingAdministrative
  t: TFunction
  fallbackEGID?: number
  entryNumber?: number
  totalEntries?: number
  isMostRecent?: boolean
  className?: string
}

const CardWithHighlight = styled(Card)<{ $highlighted?: boolean }>`
  ${({ $highlighted, theme }) =>
    $highlighted &&
    `
    border: 2px solid ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primary}20;
  `}
`

const HeaderFlex = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`

const TitleWithBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

const NoDataText = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-style: italic;
`

/**
 * Helper to check if a value is null, undefined, empty string, or the literal string "null"
 * This handles cases where the API returns "null" as a string instead of actual null
 */
const isNullOrInvalid = (value: unknown): boolean =>
  value === null || value === undefined || value === 'null' || value === ''

const SectionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  padding: 0 20px 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

export const HeatingSystemCard: React.FC<HeatingSystemCardProps> = ({
  adminData,
  t,
  fallbackEGID,
  entryNumber,
  totalEntries,
  isMostRecent,
  className,
}) => {
  const {
    egid,
    heater_generator,
    power_source_heater,
    heater_distribution,
    heater_water_generator,
    power_source_water_heater,
    additional_heater_generator,
    power_source_additional_heater,
    additional_heater_water_generator,
    power_source_additional_water_heater,
    last_update_timestamp_heater,
    last_update_timestamp_water_heater,
    last_update_timestamp_additional_heater,
    last_update_timestamp_additional_water_heater,
  } = adminData

  const effectiveEGID = egid || fallbackEGID

  // Build metrics for Primary Heating
  const primaryHeatingMetrics: InfoCardMetric[] = [
    {
      label: t('heating.metrics.generatorType'),
      value: isNullOrInvalid(heater_generator) ? (
        <NoDataText>{t('heating.metrics.notDeclared')}</NoDataText>
      ) : (
        translateGwrValue(heater_generator, 'heaterGenerator', t)
      ),
    },
    {
      label: t('heating.metrics.powerSource'),
      value: isNullOrInvalid(power_source_heater) ? (
        <NoDataText>{t('heating.metrics.notDeclared')}</NoDataText>
      ) : (
        translateGwrValue(power_source_heater, 'energySource', t)
      ),
    },
  ]

  if (heater_distribution) {
    primaryHeatingMetrics.push({
      label: t('heating.metrics.distributionSystem'),
      value: heater_distribution,
    })
  }

  if (last_update_timestamp_heater) {
    primaryHeatingMetrics.push({
      label: t('heating.metrics.lastUpdated'),
      value: (
        <DataFreshnessIndicator
          timestamp={last_update_timestamp_heater}
          t={t}
        />
      ),
    })
  }

  // Build metrics for Water Heating
  const hasWaterHeating =
    !isNullOrInvalid(heater_water_generator) ||
    !isNullOrInvalid(power_source_water_heater)
  const waterHeatingMetrics: InfoCardMetric[] = hasWaterHeating
    ? [
        {
          label: t('heating.metrics.generatorType'),
          value: isNullOrInvalid(heater_water_generator) ? (
            <NoDataText>{t('heating.metrics.notDeclared')}</NoDataText>
          ) : (
            translateGwrValue(heater_water_generator, 'heaterGenerator', t)
          ),
        },
        {
          label: t('heating.metrics.powerSource'),
          value: isNullOrInvalid(power_source_water_heater) ? (
            <NoDataText>{t('heating.metrics.notDeclared')}</NoDataText>
          ) : (
            translateGwrValue(power_source_water_heater, 'energySource', t)
          ),
        },
        ...(last_update_timestamp_water_heater
          ? [
              {
                label: t('heating.metrics.lastUpdated'),
                value: (
                  <DataFreshnessIndicator
                    timestamp={last_update_timestamp_water_heater}
                    t={t}
                  />
                ),
              },
            ]
          : []),
      ]
    : []

  // Build metrics for Additional Heating
  const hasAdditionalHeating =
    !isNullOrInvalid(additional_heater_generator) ||
    !isNullOrInvalid(power_source_additional_heater)
  const additionalHeatingMetrics: InfoCardMetric[] = hasAdditionalHeating
    ? [
        {
          label: t('heating.metrics.generatorType'),
          value: isNullOrInvalid(additional_heater_generator) ? (
            <NoDataText>{t('heating.metrics.notDeclared')}</NoDataText>
          ) : (
            translateGwrValue(additional_heater_generator, 'heaterGenerator', t)
          ),
        },
        {
          label: t('heating.metrics.powerSource'),
          value: isNullOrInvalid(power_source_additional_heater) ? (
            <NoDataText>{t('heating.metrics.notDeclared')}</NoDataText>
          ) : (
            translateGwrValue(power_source_additional_heater, 'energySource', t)
          ),
        },
        ...(last_update_timestamp_additional_heater
          ? [
              {
                label: t('heating.metrics.lastUpdated'),
                value: (
                  <DataFreshnessIndicator
                    timestamp={last_update_timestamp_additional_heater}
                    t={t}
                  />
                ),
              },
            ]
          : []),
      ]
    : []

  // Build metrics for Additional Water Heating
  const hasAdditionalWaterHeating =
    !isNullOrInvalid(additional_heater_water_generator) ||
    !isNullOrInvalid(power_source_additional_water_heater)
  const additionalWaterHeatingMetrics: InfoCardMetric[] =
    hasAdditionalWaterHeating
      ? [
          {
            label: t('heating.metrics.generatorType'),
            value: isNullOrInvalid(additional_heater_water_generator) ? (
              <NoDataText>{t('heating.metrics.notDeclared')}</NoDataText>
            ) : (
              translateGwrValue(
                additional_heater_water_generator,
                'heaterGenerator',
                t
              )
            ),
          },
          {
            label: t('heating.metrics.powerSource'),
            value: isNullOrInvalid(power_source_additional_water_heater) ? (
              <NoDataText>{t('heating.metrics.notDeclared')}</NoDataText>
            ) : (
              translateGwrValue(
                power_source_additional_water_heater,
                'energySource',
                t
              )
            ),
          },
          ...(last_update_timestamp_additional_water_heater
            ? [
                {
                  label: t('heating.metrics.lastUpdated'),
                  value: (
                    <DataFreshnessIndicator
                      timestamp={last_update_timestamp_additional_water_heater}
                      t={t}
                    />
                  ),
                },
              ]
            : []),
        ]
      : []

  const hasAdditionalSystems = hasAdditionalHeating || hasAdditionalWaterHeating

  return (
    <CardWithHighlight $highlighted={isMostRecent} className={className}>
      <CardHeader>
        <HeaderFlex>
          <TitleWithBadge>
            <CardTitle>
              <Icon name="flame" size="small" />
              {t('heating.card.title')}
              {entryNumber &&
                ` - ${t('heating.card.entryOf', { number: entryNumber, total: totalEntries })}`}
            </CardTitle>
            {effectiveEGID && (
              <Badge variant="default">EGID: {effectiveEGID}</Badge>
            )}
          </TitleWithBadge>
          {isMostRecent && (
            <Badge variant="success">{t('heating.card.mostRecent')}</Badge>
          )}
        </HeaderFlex>
      </CardHeader>

      {/* Primary Row: Primary Heating | Water Heating */}
      <SectionsGrid>
        <InfoCard
          title={t('heating.sections.primaryHeating')}
          icon="flame"
          iconColor="#ef4444"
          metrics={primaryHeatingMetrics}
        />

        {hasWaterHeating ? (
          <InfoCard
            title={t('heating.sections.waterHeating')}
            icon="droplet"
            iconColor="#3b82f6"
            metrics={waterHeatingMetrics}
          />
        ) : (
          <InfoCard
            title={t('heating.sections.waterHeating')}
            icon="droplet"
            iconColor="#3b82f6"
            metrics={[
              {
                label: t('heating.metrics.status'),
                value: (
                  <NoDataText>
                    {t('heating.metrics.noDataAvailable')}
                  </NoDataText>
                ),
              },
            ]}
          />
        )}
      </SectionsGrid>

      {/* Additional Row: Additional Heating | Additional Water Heating */}
      {hasAdditionalSystems && (
        <SectionsGrid>
          {hasAdditionalHeating ? (
            <InfoCard
              title={t('heating.sections.additionalHeating')}
              icon="flame"
              iconColor="#ef4444"
              metrics={additionalHeatingMetrics}
            />
          ) : (
            <InfoCard
              title={t('heating.sections.additionalHeating')}
              icon="flame"
              iconColor="#ef4444"
              metrics={[
                {
                  label: t('heating.metrics.status'),
                  value: (
                    <NoDataText>
                      {t('heating.metrics.noDataAvailable')}
                    </NoDataText>
                  ),
                },
              ]}
            />
          )}

          {hasAdditionalWaterHeating ? (
            <InfoCard
              title={t('heating.sections.additionalWaterHeating')}
              icon="droplet"
              iconColor="#3b82f6"
              metrics={additionalWaterHeatingMetrics}
            />
          ) : (
            <InfoCard
              title={t('heating.sections.additionalWaterHeating')}
              icon="droplet"
              iconColor="#3b82f6"
              metrics={[
                {
                  label: t('heating.metrics.status'),
                  value: (
                    <NoDataText>
                      {t('heating.metrics.noDataAvailable')}
                    </NoDataText>
                  ),
                },
              ]}
            />
          )}
        </SectionsGrid>
      )}
    </CardWithHighlight>
  )
}
